import { useState, useCallback, useMemo, useEffect } from "react";
import { IGetPresentationForEditingResponse, GetPresentationForEditingResponse } from "../../../entities-generated/models/presentation/get-presentation-for-editing-response";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrUndefined, isNotNullOrWhitespace, isNullOrUndefined, isNullOrWhitespace } from "../../../framework/utils";
import { any, firstOrDefault, none, sum, where } from "../../../framework/utils/collection";
import { PresentationEvents } from "../events";
import { IPresentationEditorFormModel, IPresentationEditorValueModel, IPresentationEditorModelChange } from "./presentation-editor-iface";
import { xlate } from "../../../framework/utils/i18n";
import { PresentationTiming } from "../../../entities-generated/common/presentation-timing";
import { ComponentModel, ComponentSource, getComponentId } from "../../markdown/parser/core";
import { toPresentationComponentModels } from "../../markdown/parser";
import { ITimingSpecComponentModel, TimingSpecComponentModel } from "../../markdown/parser/timing-spec";
import { TimingMode } from "../../../entities/timing-mode";
import { ICreatePresentationRequest } from "../../../entities-generated/models/presentation/create-presentation-request";
import { IUpdatePresentationRequest } from "../../../entities-generated/models/presentation/update-presentation-request";
import { EndingSpecMode } from "../entities/ending-spec-mode";
import { getNowUnixTimestamp, HOUR_IN_MILLISECONDS, MINUTE_IN_MILLISECONDS, SECOND_IN_MILLISECONDS, toLocalUnixTimestamp } from "../../../framework/utils/date-time";
import { IStartPresentationRequest } from "../../../entities-generated/models/presentation/start-presentation-request";
import { INoteActivePresentationSectionRequest } from "../../../entities-generated/models/presentation/note-active-presentation-section-request";


// ##BEGIN:DEVELOPERCODE##

export const MAX_DURATION = 43199;

const normalizeTitle = (t: string)  => {
    const normalized = t.replaceAll(/\s+/g, ' ').toLowerCase().trim();
    return normalized;
}

// ##END:DEVELOPERCODE##

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<IGetPresentationForEditingResponse, string> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.value = data.model.id;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }
})(
    'id' // id
    ,'id' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:title/validated/string/string%%
export const title = new (class titleService extends ValidatedFormValueService<IGetPresentationForEditingResponse, string, string> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        let t = data.model.title;
        const searchTitle = normalizeTitle(t);
        this.knownTitles = data.existingTitles;
        this.invalidTitles = where(data.existingTitles, x => x !== searchTitle);
        this.setInputValue(new BwjInput<string>(t));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.title = this.value.value;
        data.existingTitles = this.knownTitles;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        value = value.substring(0, 50);
        const result = this.requiredFieldValidator(value, xlate('Please provide a title for this discussion plan'));
        const nValue = normalizeTitle(value);
        if(any(this.invalidTitles, t => t === nValue)) {
            result.validity = BwjValidityState.Invalid;
            result.message = xlate('A discussion plan with this title already exists');
        }
        
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    private knownTitles: string[] = [];
    private invalidTitles: string[] = [];

})(
    'title' // id
    ,'Title' // description
);
// %%END:title/validated/string/string%%

// %%BEGIN:presenterDefinedDuration/validated/number | undefined/number | undefined%%
export const presenterDefinedDuration = new (class presenterDefinedDurationService extends ValidatedFormValueService<IGetPresentationForEditingResponse, number | undefined, number | undefined> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.setInputValue(new BwjInput<number | undefined>(data.model.presenterDefinedDuration ?? undefined));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.presenterDefinedDuration = this.value.value;
    }

    public getDefaultValue(): BwjInput<number | undefined> {
        return new BwjInput<number | undefined>(undefined);
    }

    public getInputValue(): BwjInput<number | undefined> {
        return this.value;
    }

    public setInputValue(value: BwjInput<number | undefined>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number | undefined {
        return this.value.value;
    }

    public validate(value: number | undefined): Promise<BwjInput<number | undefined>> {
        return Promise.resolve(this.getInputObject(value)); // always valid
    }

    public onUpdate(value: number | undefined): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            content.setInputValue(content.getInputValue());
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'presenterDefinedDuration' // id
    ,'Presenter defined duration' // description
);
// %%END:presenterDefinedDuration/validated/number | undefined/number | undefined%%

// %%BEGIN:content/mutable/string/string%%
export const content = new (class contentService extends MutableFormValueService<IGetPresentationForEditingResponse, string, string> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.value = data.model.content;
        this.setDerivedValues(data.model.content);
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.content = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

    public setInputValue(value: string): void {
        this.value = value;
        this.setDerivedValues(value);
    }

    public getOutputValue(): string {
        return this.value;
    }

    public onUpdate(value: string): void {
        this.setInputValue(value);
        !!this.publishChange && this.publishChange(this.id);
    }

    private setDerivedValues(value: string) {
        componentModel.setInputValue(toPresentationComponentModels(value));

        const timingSpecs = componentModel.getOutputValue() as ITimingSpecComponentModel[];
        if(none(timingSpecs, spec => spec.specType !== TimingMode.Preamble)) {
            timing.setInputValue(PresentationTiming.None);
        } else if (any(timingSpecs, spec => spec.specType === TimingMode.ShareTime)) {
            timing.setInputValue(PresentationTiming.Proportional);
        } else if (any(timingSpecs, spec => spec.specType === TimingMode.FlexTime)) {
            timing.setInputValue(PresentationTiming.Flexible);
        } else {
            timing.setInputValue(PresentationTiming.Firm);
        }

        const myTiming = timing.getOutputValue();

        durationSpecifiable.setInputValue(myTiming === PresentationTiming.None || myTiming === PresentationTiming.Proportional);

        const durationSpecs = where(timingSpecs, spec => spec.specType === TimingMode.FirmTime || spec.specType === TimingMode.FlexTime);
        const mySpecDuration = durationSpecs.length > 0 ?
            sum(durationSpecs, spec => (spec.primaryUnits * 60) + (spec.subUnits ?? 0)) : undefined;
        specDuration.setInputValue(mySpecDuration);
        
        if(durationSpecs.length > 0) {
            const firmTime = where(durationSpecs, spec => spec.specType === TimingMode.FirmTime);
            const myFirmTimeDuration = firmTime.length > 0 ?
                sum(firmTime, spec => (spec.primaryUnits * 60) + (spec.subUnits ?? 0)) : undefined;
            firmTimeDuration.setInputValue(myFirmTimeDuration);
        } else {
            firmTimeDuration.setInputValue(undefined);
        }

        const myDuration = (myTiming === PresentationTiming.Firm || myTiming === PresentationTiming.Flexible) ?
            mySpecDuration : presenterDefinedDuration.getOutputValue();
        duration.setInputValue(myDuration);
    }
})(
    'content' // id
    ,'Content' // description
);
// %%END:content/mutable/string/string%%

// %%BEGIN:timing/mutable/PresentationTiming/PresentationTiming%%
export const timing = new (class timingService extends MutableFormValueService<IGetPresentationForEditingResponse, PresentationTiming, PresentationTiming> {
    public initialize(data: IGetPresentationForEditingResponse): void {}

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): PresentationTiming {
        // TODO: return the default value of this property, before the model is initialized for the first time
        return PresentationTiming.None;
    }

    public getInputValue(): PresentationTiming {
        return this.value;
    }

    public setInputValue(value: PresentationTiming): void {
        this.value = value;
    }

    public getOutputValue(): PresentationTiming {
        return this.value;
    }

    public onUpdate(value: PresentationTiming): void {}
})(
    'timing' // id
    ,'Timing' // description
);
// %%END:timing/mutable/PresentationTiming/PresentationTiming%%

// %%BEGIN:durationSpecifiable/mutable/boolean/boolean%%
export const durationSpecifiable = new (class durationSpecifiableService extends MutableFormValueService<IGetPresentationForEditingResponse, boolean, boolean> {
    public initialize(data: IGetPresentationForEditingResponse): void {}

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {}
})(
    'durationSpecifiable' // id
    ,'Duration specifiable' // description
);
// %%END:durationSpecifiable/mutable/boolean/boolean%%

// %%BEGIN:duration/mutable/number | undefined/number | undefined%%
export const duration = new (class durationService extends MutableFormValueService<IGetPresentationForEditingResponse, number | undefined, number | undefined> {
    public initialize(data: IGetPresentationForEditingResponse): void {}

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): number | undefined {
        return undefined;
    }

    public getInputValue(): number | undefined {
        return this.value;
    }

    public setInputValue(value: number | undefined): void {
        this.value = value;
    }

    public getOutputValue(): number | undefined {
        return this.value;
    }

    public onUpdate(value: number | undefined): void {}
})(
    'duration' // id
    ,'Computed value.  Duration is either the value the user defined, or derived from the timing specs, whichever is most logical/possible to use. Defined in seconds' // description
);
// %%END:duration/mutable/number | undefined/number | undefined%%

// %%BEGIN:specDuration/mutable/number | undefined/number | undefined%%
export const specDuration = new (class specDurationService extends MutableFormValueService<IGetPresentationForEditingResponse, number | undefined, number | undefined> {
    public initialize(data: IGetPresentationForEditingResponse): void {}

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): number | undefined {
        return undefined;
    }

    public getInputValue(): number | undefined {
        return this.value;
    }

    public setInputValue(value: number | undefined): void {
        this.value = value;
    }

    public getOutputValue(): number | undefined {
        return this.value;
    }

    public onUpdate(value: number | undefined): void {}
})(
    'specDuration' // id
    ,'Total duration of all timing specs with explicitly defined (firm or flex) durations' // description
);
// %%END:specDuration/mutable/number | undefined/number | undefined%%

// %%BEGIN:firmTimeDuration/mutable/number | undefined/number | undefined%%
export const firmTimeDuration = new (class firmTimeDurationService extends MutableFormValueService<IGetPresentationForEditingResponse, number | undefined, number | undefined> {
    public initialize(data: IGetPresentationForEditingResponse): void {}

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): number | undefined {
        return undefined;
    }

    public getInputValue(): number | undefined {
        return this.value;
    }

    public setInputValue(value: number | undefined): void {
        this.value = value;
    }

    public getOutputValue(): number | undefined {
        return this.value;
    }

    public onUpdate(value: number | undefined): void {}
})(
    'firmTimeDuration' // id
    ,'Firm time duration' // description
);
// %%END:firmTimeDuration/mutable/number | undefined/number | undefined%%

// %%BEGIN:componentModel/mutable/ComponentModel[]/ComponentModel[]%%
export const componentModel = new (class componentModelService extends MutableFormValueService<IGetPresentationForEditingResponse, ComponentModel[], ComponentModel[]> {
    public initialize(data: IGetPresentationForEditingResponse): void {}

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): ComponentModel[] {
        return [];
    }

    public getInputValue(): ComponentModel[] {
        return this.value;
    }

    public setInputValue(value: ComponentModel[]): void {
        this.value = value;
    }

    public getOutputValue(): ComponentModel[] {
        return this.value;
    }

    public onUpdate(value: ComponentModel[]): void {}
})(
    'componentModel' // id
    ,'Component model' // description
);
// %%END:componentModel/mutable/ComponentModel[]/ComponentModel[]%%

// %%BEGIN:endingSpecMode/mutable/EndingSpecMode/EndingSpecMode%%
export const endingSpecMode = new (class endingSpecModeService extends MutableFormValueService<IGetPresentationForEditingResponse, EndingSpecMode, EndingSpecMode> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        const timingValue = timing.getOutputValue();
        const durationValue = duration.getOutputValue();
        // if user doesn't specify a duration in circumstances where this cannot be implied, duration offset end time isn't an option for him
        if(isNullOrUndefined(durationValue) && (timingValue === PresentationTiming.None || timingValue === PresentationTiming.Proportional)) {
            this.value = EndingSpecMode.ExplicitlyDefined;
        } else if (timingValue === PresentationTiming.Firm) {
            this.value = EndingSpecMode.DurationOffset;
        } else {
            this.value = EndingSpecMode.Unspecified;
        }
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): EndingSpecMode {
        return EndingSpecMode.Unspecified;
    }

    public getInputValue(): EndingSpecMode {
        return this.value;
    }

    public setInputValue(value: EndingSpecMode): void {
        this.value = value;
    }

    public getOutputValue(): EndingSpecMode {
        return this.value;
    }

    public onUpdate(value: EndingSpecMode): void {
        this.value = value;
        if(value === EndingSpecMode.ExplicitlyDefined) {
            minEndDateTime.updateValue();
            maxEndDateTime.updateValue();
        }
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'endingSpecMode' // id
    ,'Ending spec mode' // description
);
// %%END:endingSpecMode/mutable/EndingSpecMode/EndingSpecMode%%

// %%BEGIN:presenterDefinedEndTime/validated/number/number%%
export const presenterDefinedEndTime = new (class presenterDefinedEndTimeService extends ValidatedFormValueService<IGetPresentationForEditingResponse, number, number> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        const value = data.model.endTime !== undefined ? toLocalUnixTimestamp(data.model.endTime) : 0;
        this.setInputValue(new BwjInput<number>(value));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): BwjInput<number> {
        return new BwjInput<number>(0);
    }

    public getInputValue(): BwjInput<number> {
        return this.value;
    }

    public setInputValue(value: BwjInput<number>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        return this.value.value;
    }

    public validate(value: number): Promise<BwjInput<number>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: number): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            minEndDateTime.updateValue();
            maxEndDateTime.updateValue();
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'presenterDefinedEndTime' // id
    ,'Presenter defined end time' // description
);
// %%END:presenterDefinedEndTime/validated/number/number%%

// %%BEGIN:hasMultipleEndTimeOptions/immutable/boolean%%
export const hasMultipleEndTimeOptions = new (class hasMultipleEndTimeOptionsService extends ImmutableFormValueService<IGetPresentationForEditingResponse, boolean> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.value = endingSpecMode.getOutputValue() === EndingSpecMode.Unspecified;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

})(
    'hasMultipleEndTimeOptions' // id
    ,'Has multiple end time options' // description
);
// %%END:hasMultipleEndTimeOptions/immutable/boolean%%

// %%BEGIN:minEndDateTime/mutable/number/number%%
export const minEndDateTime = new (class minEndDateTimeService extends MutableFormValueService<IGetPresentationForEditingResponse, number, number> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.updateValue();
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

    public setInputValue(value: number): void {
        this.value = value;
    }

    public updateValue() {
        const firmTime = firmTimeDuration.getOutputValue();
        const now = getNowUnixTimestamp();
        if(isNotNullOrUndefined(firmTime)) {
            this.value = now + (firmTime! * SECOND_IN_MILLISECONDS);
        } else {
            this.value = now - (now % MINUTE_IN_MILLISECONDS) + MINUTE_IN_MILLISECONDS;
        }
    }

    public getOutputValue(): number {
        return this.value;
    }

    public onUpdate(value: number): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'minEndDateTime' // id
    ,'Min end datetime' // description
);
// %%END:minEndDateTime/mutable/number/number%%

// %%BEGIN:maxEndDateTime/mutable/number/number%%
export const maxEndDateTime = new (class maxEndDateTimeService extends MutableFormValueService<IGetPresentationForEditingResponse, number, number> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.updateValue();
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

    public setInputValue(value: number): void {
        this.value = value;
    }

    public getOutputValue(): number {
        return this.value;
    }

    public updateValue() {
        const minDateTime = minEndDateTime.getOutputValue();
        const floorHour = Math.floor(minDateTime / HOUR_IN_MILLISECONDS) * HOUR_IN_MILLISECONDS;
        this.value = floorHour + (HOUR_IN_MILLISECONDS * 12) - MINUTE_IN_MILLISECONDS;
    }

    public onUpdate(value: number): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'maxEndDateTime' // id
    ,'Max end datetime' // description
);
// %%END:maxEndDateTime/mutable/number/number%%

// %%BEGIN:currentSection/mutable/ITimingSpecComponentModel/string | undefined%%
export const currentSection = new (class currentSectionService extends MutableFormValueService<IGetPresentationForEditingResponse, ITimingSpecComponentModel, string | undefined> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        const sections = componentModel.getOutputValue();
        const cs = data.model.currentSection;
        const section = isNotNullOrWhitespace(cs) ? firstOrDefault(sections, s => getComponentId(s) === cs) : null;
        this.value = section as ITimingSpecComponentModel ?? new TimingSpecComponentModel(new ComponentSource());
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.currentSection = this.value.startLineNumber !== 0 ? getComponentId(this.value) : undefined;
    }

    public getDefaultValue(): ITimingSpecComponentModel {
        return new TimingSpecComponentModel(new ComponentSource());
    }

    public getInputValue(): ITimingSpecComponentModel {
        return this.value;
    }

    public setInputValue(value: ITimingSpecComponentModel): void {
        this.value = value;
    }

    public getOutputValue(): string | undefined {
        const value = this.value;
        return value.startLineNumber !== 0 ? getComponentId(value) : undefined;
    }

    public onUpdate(value: ITimingSpecComponentModel): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'currentSection' // id
    ,'Current section' // description
);
// %%END:currentSection/mutable/ITimingSpecComponentModel/string | undefined%%

// %%BEGIN:currentSectionStart/mutable/number/number%%
export const currentSectionStart = new (class currentSectionStartService extends MutableFormValueService<IGetPresentationForEditingResponse, number, number> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.value = data.model.sectionStartTime ?? 0;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.sectionStartTime = this.value !== 0 ? this.value : undefined;
    }

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

    public setInputValue(value: number): void {
        this.value = value;
    }

    public getOutputValue(): number {
        return this.value;
    }

    public onUpdate(value: number): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'currentSectionStart' // id
    ,'Current section start' // description
);
// %%END:currentSectionStart/mutable/number/number%%

// %%BEGIN:currentSectionDuration/mutable/number/number%%
export const currentSectionDuration = new (class currentSectionDurationService extends MutableFormValueService<IGetPresentationForEditingResponse, number, number> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.value = 0;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {}

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

    public setInputValue(value: number): void {
        this.value = value;
    }

    public getOutputValue(): number {
        return this.value;
    }

    public onUpdate(value: number): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'currentSectionDuration' // id
    ,'Current section duration' // description
);
// %%END:currentSectionDuration/mutable/number/number%%

// %%BEGIN:endTime/mutable/number/number%%
export const endTime = new (class endTimeService extends MutableFormValueService<IGetPresentationForEditingResponse, number, number> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.value = data.model.endTime ?? 0;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.endTime = this.value !== 0 ? this.value : undefined;
    }

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

    public setInputValue(value: number): void {
        this.value = value;
    }

    public getOutputValue(): number {
        return this.value;
    }

    public onUpdate(value: number): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'endTime' // id
    ,'End time' // description
);
// %%END:endTime/mutable/number/number%%

// %%BEGIN:actualDuration/mutable/number | undefined/number | undefined%%
export const actualDuration = new (class actualDurationService extends MutableFormValueService<IGetPresentationForEditingResponse, number | undefined, number | undefined> {
    public initialize(data: IGetPresentationForEditingResponse): void {
        this.value = data.model.actualDuration;
    }

    public applyToDataModel(data: IGetPresentationForEditingResponse): void {
        data.model.actualDuration = this.value;
    }

    public getDefaultValue(): number | undefined {
        return undefined;
    }

    public getInputValue(): number | undefined {
        return this.value;
    }

    public setInputValue(value: number | undefined): void {
        this.value = value;
    }

    public getOutputValue(): number | undefined {
        return this.value;
    }

    public onUpdate(value: number | undefined): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'actualDuration' // id
    ,'Actual duration' // description
);
// %%END:actualDuration/mutable/number | undefined/number | undefined%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IPresentationEditorFormModel => {
    return {
       title: title.getInputValue(),
       presenterDefinedDuration: presenterDefinedDuration.getInputValue(),
       content: content.getInputValue(),
       timing: timing.getInputValue(),
       durationSpecifiable: durationSpecifiable.getInputValue(),
       duration: duration.getInputValue(),
       specDuration: specDuration.getInputValue(),
       firmTimeDuration: firmTimeDuration.getInputValue(),
       componentModel: componentModel.getInputValue(),
       endingSpecMode: endingSpecMode.getInputValue(),
       presenterDefinedEndTime: presenterDefinedEndTime.getInputValue(),
       hasMultipleEndTimeOptions: hasMultipleEndTimeOptions.getInputValue(),
       minEndDateTime: minEndDateTime.getInputValue(),
       maxEndDateTime: maxEndDateTime.getInputValue(),
       currentSection: currentSection.getInputValue(),
       currentSectionStart: currentSectionStart.getInputValue(),
       currentSectionDuration: currentSectionDuration.getInputValue(),
       endTime: endTime.getInputValue(),
       actualDuration: actualDuration.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IPresentationEditorValueModel => {
    return {
       id: id.getOutputValue(),
       title: title.getOutputValue(),
       presenterDefinedDuration: presenterDefinedDuration.getOutputValue(),
       content: content.getOutputValue(),
       timing: timing.getOutputValue(),
       duration: duration.getOutputValue(),
       specDuration: specDuration.getOutputValue(),
       firmTimeDuration: firmTimeDuration.getOutputValue(),
       componentModel: componentModel.getOutputValue(),
       endingSpecMode: endingSpecMode.getOutputValue(),
       presenterDefinedEndTime: presenterDefinedEndTime.getOutputValue(),
       currentSection: currentSection.getOutputValue(),
       currentSectionStart: currentSectionStart.getOutputValue(),
       endTime: endTime.getOutputValue(),
       actualDuration: actualDuration.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        title.getInputValue(),
        presenterDefinedDuration.getInputValue(),
        presenterDefinedEndTime.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IPresentationEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(PresentationEvents.FormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IPresentationEditorFormModel>> => {
    const promises = [
        title.formValidateAndUpdate(),
        presenterDefinedDuration.formValidateAndUpdate(),
        presenterDefinedEndTime.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IPresentationEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IPresentationEditorValueModel, dataModel: IGetPresentationForEditingResponse } = {
    dataModel: new GetPresentationForEditingResponse()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IGetPresentationForEditingResponse) => {
    const dataModel = data || new GetPresentationForEditingResponse();

    id.initialize(dataModel);
    title.initialize(dataModel);
    presenterDefinedDuration.initialize(dataModel);
    content.initialize(dataModel);
    timing.initialize(dataModel);
    durationSpecifiable.initialize(dataModel);
    duration.initialize(dataModel);
    specDuration.initialize(dataModel);
    firmTimeDuration.initialize(dataModel);
    componentModel.initialize(dataModel);
    endingSpecMode.initialize(dataModel);
    presenterDefinedEndTime.initialize(dataModel);
    hasMultipleEndTimeOptions.initialize(dataModel);
    minEndDateTime.initialize(dataModel);
    maxEndDateTime.initialize(dataModel);
    currentSection.initialize(dataModel);
    currentSectionStart.initialize(dataModel);
    currentSectionDuration.initialize(dataModel);
    endTime.initialize(dataModel);
    actualDuration.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IPresentationEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
title.setChangePublisher(broadcastChange);
presenterDefinedDuration.setChangePublisher(broadcastChange);
content.setChangePublisher(broadcastChange);
timing.setChangePublisher(broadcastChange);
durationSpecifiable.setChangePublisher(broadcastChange);
duration.setChangePublisher(broadcastChange);
specDuration.setChangePublisher(broadcastChange);
firmTimeDuration.setChangePublisher(broadcastChange);
componentModel.setChangePublisher(broadcastChange);
endingSpecMode.setChangePublisher(broadcastChange);
presenterDefinedEndTime.setChangePublisher(broadcastChange);
minEndDateTime.setChangePublisher(broadcastChange);
maxEndDateTime.setChangePublisher(broadcastChange);
currentSection.setChangePublisher(broadcastChange);
currentSectionStart.setChangePublisher(broadcastChange);
currentSectionDuration.setChangePublisher(broadcastChange);
endTime.setChangePublisher(broadcastChange);
actualDuration.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new GetPresentationForEditingResponse();

    id.applyToDataModel(dataModel);
    title.applyToDataModel(dataModel);
    presenterDefinedDuration.applyToDataModel(dataModel);
    content.applyToDataModel(dataModel);
    timing.applyToDataModel(dataModel);
    durationSpecifiable.applyToDataModel(dataModel);
    duration.applyToDataModel(dataModel);
    specDuration.applyToDataModel(dataModel);
    firmTimeDuration.applyToDataModel(dataModel);
    componentModel.applyToDataModel(dataModel);
    endingSpecMode.applyToDataModel(dataModel);
    presenterDefinedEndTime.applyToDataModel(dataModel);
    hasMultipleEndTimeOptions.applyToDataModel(dataModel);
    minEndDateTime.applyToDataModel(dataModel);
    maxEndDateTime.applyToDataModel(dataModel);
    currentSection.applyToDataModel(dataModel);
    currentSectionStart.applyToDataModel(dataModel);
    currentSectionDuration.applyToDataModel(dataModel);
    endTime.applyToDataModel(dataModel);
    actualDuration.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IPresentationEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], PresentationEvents.FormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IPresentationEditorFormModel => {
    const [getter, setter] = useState<IPresentationEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IPresentationEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IPresentationEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('PresentationEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('PresentationEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toCreateRequest = (): ICreatePresentationRequest => {
    const v = toValueModel();
    const request: ICreatePresentationRequest = {
        timing: v.timing,
        title: v.title,
        content: v.content,
        duration: v.duration,
        firmTimeDuration: v.firmTimeDuration,
        presenterDefinedDuration: v.presenterDefinedDuration,
    };

    return request;
};

export const toUpdateRequest = (): IUpdatePresentationRequest => {
    const v = toValueModel();
    const request: IUpdatePresentationRequest = {
        id: v.id,
        timing: v.timing,
        title: v.title,
        content: v.content,
        duration: v.duration,
        firmTimeDuration: v.firmTimeDuration,
        presenterDefinedDuration: v.presenterDefinedDuration,
    };

    return request;
};

export const toStartPresentationRequest = (): IStartPresentationRequest => {
    const v = toValueModel();
    const request: IStartPresentationRequest = {
        sectionId: v.currentSection ?? '0-0',
        startTime: v.currentSectionStart,
        endTime: v.endTime
    };

    return request;
};

export const toNoteActivePresentationSectionRequest = (): INoteActivePresentationSectionRequest => {
    const v = toValueModel();
    const request: INoteActivePresentationSectionRequest = {
        sectionId: v.currentSection ?? '',
        startTime: v.currentSectionStart,
    };

    return request;
};

export const markAllChangesSaved = () => {
    setReversionState();
    revertChanges();
}

export const initializeNewPresentation = (data?: IGetPresentationForEditingResponse) => {
    const dataModel = data || new GetPresentationForEditingResponse();
    initializeService(dataModel);

    let t = title.getOutputValue();
    if(isNullOrWhitespace(t)) {
        t = xlate('My Presentation');
        const nt = normalizeTitle(t);
        if(any(dataModel.existingTitles, et => et === nt)) {
            let titleCount = 2;
            while(true) {
                t = xlate('My Presentation ({titleCount})', { titleCount });
                const normTitle = normalizeTitle(t);
                if(none(dataModel.existingTitles, et => et === normTitle)) {
                    break;
                }
                titleCount++;
            }
        }

        title.onUpdate(t);
    }
};

// ##END:DEVELOPERCODE##
